import { Avatar, Radio } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import EntriliaLogo from "@entrilia/shared/images/greylogo.png";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { Field } from "../../../../../api/types/objectTypes";
import type { WorkflowActivityChangeSet } from "../../../../../api/types/workflowTypes";
import EntityValueGridCell from "../../../../entityFields/EntityValueGridCell";
import { fieldTypeToGridColTypeMap } from "../../../../entityFields/helpers";
import ObjectPageLink from "../../../../entityFields/ObjectPageLink";

export interface WorkflowActivityChangeSetRow extends WorkflowActivityChangeSet {
  isSelected?: boolean;
}

const getSelectionColumn = (
  onSelectChangeSet: (changeSet: WorkflowActivityChangeSet) => void
): GridColDef<WorkflowActivityChangeSetRow> => ({
  field: "_",
  headerName: "",
  resizable: false,
  width: 50,
  align: "center",
  renderCell: ({ row }) =>
    row.action === "UseExisting" ? (
      <Radio checked={row.isSelected} onClick={() => !row.isSelected && onSelectChangeSet(row)} />
    ) : null,
});

const getLogoColumn = (): GridColDef<WorkflowActivityChangeSetRow> => ({
  field: "LogoUrl",
  headerName: "",
  resizable: false,
  width: 72,
  renderCell: ({ row, field }) => (
    <Avatar
      src={stringifyValue(row.fields.find((f) => f.name === field)?.newValue) || EntriliaLogo}
      variant="rounded"
      alt="logo"
      slotProps={{ img: { sx: { objectFit: "contain" } } }}
      sx={{ width: 60, height: 24 }}
    />
  ),
});

const getGridColumnFromFieldDefinition = (fieldDefinition: Field): GridColDef<WorkflowActivityChangeSetRow> => ({
  field: fieldDefinition.id,
  headerName: fieldDefinition.name,
  type: fieldTypeToGridColTypeMap[fieldDefinition.type],
  flex: 1,
  minWidth: 100,
  renderCell: ({ row, field }) => {
    const value = row.fields.find((f) => f.name === field)?.newValue;
    if (value === undefined || value === null) {
      return null;
    }

    if (field === "Name" && row.objectId && row.objectType) {
      return (
        <ObjectPageLink objectId={row.objectId} objectType={row.objectType} entityTypeCode={row.entityTypeCode}>
          {stringifyValue(value)}
        </ObjectPageLink>
      );
    }

    return (
      <EntityValueGridCell type={fieldDefinition.type} value={value} configuration={fieldDefinition.configuration} />
    );
  },
});

export const getColumnsFromFieldDefinitions = (
  fieldDefinitions: Field[],
  onSelectChangeSet?: (changeSet: WorkflowActivityChangeSet) => void
): GridColDef<WorkflowActivityChangeSetRow>[] => {
  const logoUrlField = fieldDefinitions.find((field) => field.id === "LogoUrl");
  const selectionColumns = onSelectChangeSet ? [getSelectionColumn(onSelectChangeSet)] : [];
  const staticColumns: GridColDef<WorkflowActivityChangeSetRow>[] = logoUrlField ? [getLogoColumn()] : [];
  const fieldColumns = fieldDefinitions
    .filter((field) => field.id !== "LogoUrl")
    .map((field) => getGridColumnFromFieldDefinition(field));
  return [...selectionColumns, ...staticColumns, ...fieldColumns];
};
